import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../utility/axiosInstance';
import { SETUP_WATCH_EVERY_MS, SETUP_WATCH_MS, setupSettled } from '../components/DatabaseData/dataView';

/**
 * Keeps a new single machine's `data_view` current while its data view setup finishes (DESIGN_V1.md D6).
 *
 * WHY. The databases page marks a single machine ready as soon as its VM is up (the page's own poll), minutes before
 * the machine's boot script installs the data view's helper and reports the result. The row in the store keeps the
 * `data_view: false` it had then, so "View data" would stay locked, until a reload, on exactly the new database it
 * was made for. So each row the poll marks ready with `data_view: false` is handed to `watch`, and re-read here every
 * 15 s, for at most 15 minutes, until `data_view` is no longer false.
 *
 * Reads only: GET /v1/databases/:id is a row of redu's own database, never a request to the machine.
 */
export default function useDataViewSetupWatch(
    onSettled: (id: number, dataView: boolean | undefined) => void,
): (id: number) => void {
    // id -> when the wait started
    const [watching, setWatching] = useState<Record<number, number>>({});
    const onSettledRef = useRef(onSettled);
    onSettledRef.current = onSettled;

    const watch = useCallback((id: number) => {
        setWatching((w) => (w[id] !== undefined ? w : { ...w, [id]: Date.now() }));
    }, []);

    useEffect(() => {
        const ids = Object.keys(watching).map(Number);
        if (ids.length === 0) return;
        let active = true;
        const stop = (id: number) => setWatching((w) => {
            if (w[id] === undefined) return w;
            const next = { ...w };
            delete next[id];
            return next;
        });
        const timer = setInterval(() => {
            ids.forEach(async (id) => {
                if (Date.now() - watching[id] > SETUP_WATCH_MS) { stop(id); return; }
                try {
                    const res = await axiosInstance.get(`/v1/databases/${id}`);
                    if (!active) return;
                    const dataView = res.data?.data_view;
                    if (!setupSettled(dataView)) return;
                    onSettledRef.current(id, typeof dataView === 'boolean' ? dataView : undefined);
                    stop(id);
                } catch (err: any) {
                    // Deleted meanwhile: nothing is left to wait for. Anything else is tried again next time.
                    if (active && err?.response?.status === 404) stop(id);
                }
            });
        }, SETUP_WATCH_EVERY_MS);
        return () => { active = false; clearInterval(timer); };
    }, [watching]);

    return watch;
}
